<template>
	<div v-if="type == 'background'" :class="item.backgroundType">
		<div class="row">
			<div class="columns column6 see-through" />
			<div class="columns column6 bg-column">
				<div class="contentblock-content">
					<h2>{{ item.title || item.header }}</h2>
					<h3 v-if="item.subtitle" class="subtitle">{{ item.subtitle }}</h3>
					<div v-parse-links v-html="item.content" />
				</div>
			</div>
			<div class="contentblock-bg">
				<picture>
					<source :srcset="item.backgroundImageWebp" type="image/webp" />
					<source :srcset="item.backgroundImage" />
					<img :src="item.backgroundImage" :alt="item.backgroundImageAlt" loading="lazy" />
				</picture>
			</div>
		</div>
	</div>
	<div
		v-else
		:class="{
			'has-big-image': item.image,
			'has-small-image': item.imageSmaller,
			'only-content': !item.image && !item.imageSmaller,
		}"
	>
		<div class="row">
			<div v-if="item.image" class="big-image col-wrap">
				<div class="columns column6 image-wrapper">
					<picture>
						<source :srcset="item.imageWebp" type="image/webp" />
						<source :srcset="item.image" />
						<img :src="item.image" :alt="item.imageAlt" loading="lazy" />
					</picture>
				</div>
				<div class="columns column6">
					<div class="contentblock-content">
						<h2>{{ item.title || item.header }}</h2>
						<h3 v-if="item.subtitle" class="subtitle">{{ item.subtitle }}</h3>
						<div v-parse-links v-html="item.content" />
					</div>
				</div>
			</div>
			<div v-else-if="item.imageSmaller" class="small-image col-wrap">
				<div class="columns column6 image-wrapper">
					<picture>
						<source :srcset="item.imageSmallerWebp" type="image/webp" />
						<source :srcset="item.imageSmaller" />
						<img :src="item.imageSmaller" :alt="item.imageSmallerAlt" loading="lazy" />
					</picture>
				</div>
				<div class="columns column6">
					<div class="contentblock-content">
						<h2>{{ item.title || item.header }}</h2>
						<h3 v-if="item.subtitle" class="subtitle">{{ item.subtitle }}</h3>
						<div v-parse-links v-html="item.content" />
					</div>
				</div>
			</div>
			<div v-else>
				<div class="columns column12 align-center">
					<div class="contentblock-content">
						<h2>{{ item.title || item.header }}</h2>
						<h3 v-if="item.subtitle" class="subtitle">{{ item.subtitle }}</h3>
						<div v-parse-links v-html="item.content" />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
defineProps({
	item: { type: Object, default: () => ({}) },
	type: { type: String, default: '' },
});
</script>

<style lang="scss" scoped>
.contentblock {
	background: var(--primary-background-color);

	.row {
		max-width: 1700px;
		width: 100%;
	}

	.only-content {
		width: 100%;

		div {
			width: 100%;
		}

		h2,
		h3 {
			color: #000;
		}
	}

	.small-image {
		.contentblock-content {
			padding: 20px 90px 20px 0;
		}

		.image-wrapper {
			padding: 90px 140px;
		}

		img {
			object-fit: cover;
			width: 100%;
			height: 100%;
		}

		h2,
		h3 {
			color: var(--header-color-quaternary);
		}
	}

	.has-big-image {
		background: var(--header-color-quinary);
		color: var(--body-color);

		h2,
		h3,
		a,
		:deep(a) {
			color: var(--body-color);
		}

		.row {
			max-width: 100%;
			width: 100vw;
		}

		.column6:first-child {
			padding: 0;
		}

		.column6:last-child {
			max-width: 750px;
			margin: 0 auto;
		}
	}

	.regular {
		.see-through {
			display: none;
		}

		.column6 {
			margin: 200px auto;
			background: rgb(255 255 255 / 80%);
			text-align: center;

			.contentblock-content {
				padding: 90px;
			}

			h2,
			h3 {
				margin: 0 auto 20px;
				max-width: 420px;
			}
		}

		h2,
		h3 {
			color: #000;
		}
	}

	/* stylelint-disable selector-class-pattern */
	.seeThrough {
		display: flex;
		flex-flow: row wrap;
		justify-content: center;
		align-items: stretch;
		margin: 0 auto;
		padding: 75px 60px;

		.see-through {
			display: block;
			border: 100px solid #fff;
			border-right: 200px solid #fff;
		}

		.bg-column {
			background: #fff;
			padding: 120px 90px 120px 0;
		}

		h2,
		h3 {
			color: var(--header-color-senary);
		}
	}
	/* stylelint-enable selector-class-pattern */

	&:nth-child(odd) {
		.col-wrap {
			flex-direction: row-reverse;
		}

		.small-image {
			h2,
			h3 {
				color: var(--header-color-tertiary);
			}

			.contentblock-content {
				padding: 20px 0 20px 90px;
			}
		}

		.only-content {
			background: var(--primary-background-color);

			h2,
			h3 {
				color: #000;
			}

			:deep(.button) {
				&:hover {
					color: var(--body-color);
					border-color: var(--body-color);
				}
			}
		}

		.has-big-image {
			background: var(--header-color-quaternary);
			color: #fff;

			h2,
			h3 {
				color: #fff;
			}

			a,
			:deep(a) {
				color: #fff;
			}
		}
	}

	> div {
		&.has-big-image {
			max-width: 100%;
			width: 100%;
		}
	}

	h2 {
		color: var(--header-color-octonary);
		max-width: 420px;
		margin: 0 0 20px;
	}

	.subtitle {
		margin: 0 0 10px;
	}

	.column6 {
		position: relative;
		z-index: 2;
	}

	.contentblock-bg {
		position: absolute;
		inset: 0;
		z-index: 1;

		img {
			object-fit: cover;
			width: 100%;
			height: 100%;
		}
	}

	.col-wrap {
		display: flex;
		flex-flow: row wrap;
		align-items: center;
		justify-content: space-between;
		width: 100%;
	}

	.column12 {
		padding: 75px 20px;

		h2,
		h3 {
			margin: 0 auto 20px;
		}

		:deep(p) {
			max-width: 600px;
			margin: 20px auto;
		}
	}

	.big-image {
		img {
			object-fit: cover;
			width: 100%;
			height: 100%;
		}
	}

	.contentblock-content {
		padding: 20px 50px;
	}
}

@media (max-width: 1080px) {
	.contentblock {
		> div {
			max-width: 700px;

			&.has-big-image {
				max-width: 100%;
				width: 100%;
			}
		}

		.contentblock-content {
			padding: 20px;
		}

		.small-image {
			.contentblock-content {
				padding: 20px 20px 40px;
			}

			.image-wrapper {
				padding: 50px 20px;
				max-width: 750px;
				margin: 0 auto;
			}

			img {
				object-fit: cover;
				width: 100%;
				height: 100%;
			}
		}

		.has-big-image {
			.big-image {
				flex-flow: column-reverse wrap;
				padding: 40px 0;
			}

			.column6:first-child {
				padding: 20px;
			}
		}

		&:nth-child(odd) {
			.small-image {
				.contentblock-content {
					padding: 20px 20px 40px;
				}
			}
		}

		/* stylelint-disable selector-class-pattern */
		.seeThrough {
			flex-flow: column-reverse wrap;
			padding: 20px;

			.bg-column {
				padding: 30px 20px 0;
			}

			.see-through {
				min-height: 500px;
				border: 40px solid #fff;
				border-top: 20px solid #fff;
			}
		}
		/* stylelint-enable selector-class-pattern */

		.regular {
			.column6 {
				width: calc(100% - 40px);
				margin: 90px auto;

				.contentblock-content {
					padding: 40px 20px;
				}
			}
		}
	}
}
</style>
